/**
 * The stones' palette.
 *
 * Five jewel tones, evenly spread around the wheel and all held at a high, even
 * saturation: gold, emerald, sapphire, amethyst, ruby. The spread is what makes
 * a filled face read — five neighbouring hues muddle into one mass at this size
 * — and the even saturation and value is what keeps them a set rather than a
 * scatter, with none jumping forward of the others.
 *
 * They are deliberately deep rather than bright. A faceted stone lightens a
 * long way once it is lit, so a colour that looks right flat comes out pastel
 * on the board.
 *
 * A piece is dealt one tone and keeps it: the stones you drop stay the colour
 * they were in your hand.
 */
export const GEM_TONES = [
  0xe8a400, // gold
  0x18a83f, // emerald
  0x1668d8, // sapphire
  0x8c30cc, // amethyst
  0xcc1f36, // ruby
];

export const TONE_COUNT = GEM_TONES.length;

/** Indices into `GEM_TONES`, by name, so orderings can be written readably. */
export const TONE = {
  gold: 0,
  emerald: 1,
  sapphire: 2,
  amethyst: 3,
  ruby: 4,
};

/**
 * The colours fireworks are drawn from: red, yellow, blue, green, purple.
 *
 * A set, not a running order — `fireworkToneSequence` decides the order, freshly
 * each time. Written out in that order anyway because it is the readable way to say
 * which five, and it is what the tests check the hues against.
 */
export const FIREWORK_TONES = [
  TONE.ruby,
  TONE.gold,
  TONE.sapphire,
  TONE.emerald,
  TONE.amethyst,
];

/** Fisher-Yates, on a copy, with the source of randomness handed in. */
function shuffled(items, random) {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * The colours for one salvo of `count` shells, in the order they go up.
 *
 * Shuffled per salvo, so two combos in a row do not fire the same sequence. What it
 * is *not* is a tone drawn per shell: that comes up three reds in a row often
 * enough to look broken rather than random, because a salvo is only a few shells
 * long and there is no run for the average to show up over. This deals from a
 * shuffled deck instead, which keeps both properties the old fixed order had — any
 * five consecutive shells are the whole palette exactly once, and no two shells in a
 * row share a colour — while giving a different arrangement every time.
 *
 * A combo past five shells starts a second shuffled cycle. That is the one place the
 * guarantee could break, since a fresh shuffle is free to open on the colour the
 * last one closed with, so the boundary is checked and swapped out.
 *
 * `random` is injectable so the sequence can be tested deterministically; nothing in
 * the game passes it.
 *
 * @param {number} count how many shells
 * @param {() => number} [random] uniform [0, 1)
 * @returns {number[]} indices into `GEM_TONES`
 */
export function fireworkToneSequence(count, random = Math.random) {
  const out = [];
  while (out.length < count) {
    const cycle = shuffled(FIREWORK_TONES, random);
    if (out.length && cycle[0] === out[out.length - 1]) {
      // Swap the collision to somewhere else in the cycle. Anywhere but the front
      // does, and the cycle stays a permutation.
      const to = 1 + Math.floor(random() * (cycle.length - 1));
      [cycle[0], cycle[to]] = [cycle[to], cycle[0]];
    }
    out.push(...cycle);
  }
  return out.slice(0, count);
}

export function randomTone() {
  return Math.floor(Math.random() * TONE_COUNT);
}
